// AGC: Inputs Service - Fetches inputs from admin catalog for task assignment
// AGC: Used by TaskDetail to populate the input selection dropdown

import { getBackendUrl } from '../utils/backendUrl'
import type { InputMaster } from '../types/inventory'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

/**
 * AGC: Fetch active inputs from admin catalog
 * AGC: These are the inputs available for assignment to tasks
 * @param token - Authentication token
 * @returns Array of active InputMaster items
 */
export async function getActiveInputs(token?: string | null): Promise<InputMaster[]> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // AGC: Fetch active inputs only from admin config endpoint
    const response = await fetch(`${BASE_URL}/api/v1/admin/config/inputs?activeOnly=true`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      console.error('AGC: Failed to fetch inputs:', response.statusText)
      return []
    }

    const data = await response.json()
    
    if (data.success && data.data?.inputs) {
      console.log(`✅ AGC: [INPUTS_SERVICE] Loaded ${data.data.inputs.length} active inputs`)
      return data.data.inputs
    }

    return []
  } catch (error) {
    console.error('❌ AGC: [INPUTS_SERVICE] Error fetching inputs:', error)
    return []
  }
}

/**
 * AGC: Get a single input by ID
 * AGC: Useful for displaying input details when editing a task
 * @param inputId - The input ID to fetch
 * @param token - Authentication token
 * @returns InputMaster or null if not found
 */
export async function getInputById(
  inputId: string,
  token?: string | null
): Promise<InputMaster | null> {
  try {
    const inputs = await getActiveInputs(token)
    return inputs.find(input => input.id === inputId) || null
  } catch (error) {
    console.error('❌ AGC: [INPUTS_SERVICE] Error fetching input by ID:', error)
    return null
  }
}

/**
 * AGC: Search inputs by name (case-insensitive)
 * AGC: Useful for autocomplete/search functionality
 * @param searchTerm - The search term
 * @param token - Authentication token
 * @returns Filtered array of InputMaster items
 */
export async function searchInputs(
  searchTerm: string,
  token?: string | null
): Promise<InputMaster[]> {
  try {
    const inputs = await getActiveInputs(token)
    const lowerSearch = searchTerm.toLowerCase()
    
    return inputs.filter(input => 
      input.name.toLowerCase().includes(lowerSearch) ||
      input.activeIngredient?.toLowerCase().includes(lowerSearch) ||
      input.commercialName?.toLowerCase().includes(lowerSearch)
    )
  } catch (error) {
    console.error('❌ AGC: [INPUTS_SERVICE] Error searching inputs:', error)
    return []
  }
}

/**
 * AGC: Create a new input in admin catalog + inventory item + optional initial stock
 */
export async function createInputAndInventoryItem(
  data: { name: string; category: string; unit: string; costPerUnit?: number; initialQuantity?: number; minimumStock?: number },
  token?: string | null
): Promise<{ inputId: string; inventoryItemId: string } | null> {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    // AGC: 1. Create admin catalog entry
    const catalogRes = await fetch(`${BASE_URL}/api/v1/admin/config/inputs`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: data.name,
        category: data.category,
        unit: data.unit,
        costPerUnit: data.costPerUnit || 0,
        isActive: true,
      }),
    })
    const catalogData = await catalogRes.json()
    const inputId = catalogData?.data?.id || data.name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_')

    // AGC: 2. Create inventory item with quantity=0 — initial stock added via movement below
    const invRes = await fetch(`${BASE_URL}/api/v1/inventory`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: data.name,
        inputId,
        category: data.category,
        unit: data.unit,
        quantity: 0,
        minimumStock: data.minimumStock || 0,
        costPerUnit: data.costPerUnit || 0,
      }),
    })
    const invData = await invRes.json()
    const inventoryItemId = invData?.data?.id || ''

    // AGC: 3. Record initial stock movement if quantity > 0
    if ((data.initialQuantity || 0) > 0) {
      await fetch(`${BASE_URL}/api/v1/inventory/movement`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          inventoryItemId,
          type: 'entry',
          quantity: data.initialQuantity,
          unit: data.unit,
          reason: 'Estoque inicial',
          costPerUnit: data.costPerUnit || 0,
        }),
      }).catch(() => {})
    }

    return { inputId, inventoryItemId }
  } catch (error) {
    console.error('❌ AGC: [INPUTS_SERVICE] Error creating input + inventory:', error)
    return null
  }
}

/**
 * AGC: Get inputs grouped by category
 * AGC: Useful for displaying inputs in categorized dropdown
 * @param token - Authentication token
 * @returns Map of category to InputMaster array
 */
export async function getInputsByCategory(
  token?: string | null
): Promise<Map<string, InputMaster[]>> {
  try {
    const inputs = await getActiveInputs(token)
    const grouped = new Map<string, InputMaster[]>()
    
    for (const input of inputs) {
      const category = input.category || 'other'
      const existing = grouped.get(category) || []
      grouped.set(category, [...existing, input])
    }
    
    return grouped
  } catch (error) {
    console.error('❌ AGC: [INPUTS_SERVICE] Error grouping inputs:', error)
    return new Map()
  }
}

